import React, { useState, useEffect, useCallback } from "react";
import "./Testimonials.css";
import Icon from "../Icon";
import { faChevronLeft, faChevronRight, faStar } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarOutline } from "@fortawesome/free-regular-svg-icons";

const testimonials = [
  {
    name: "Alla K.",
    rating: 5,
    text: `I’m very happy with my experience at this dental office! The entire staff — from the receptionists to the dental assistants — is professional, kind, and attentive.
The office is clean, comfortable, and equipped with modern technology, creating a calm and welcoming atmosphere.

Special thanks to Dr. Alina, who is an excellent dentist. She explains every step of the treatment clearly, works gently, and truly cares about her patients. You can feel her kindness and professionalism in every detail.

I highly recommend this office to anyone looking for top-quality dental care and a caring medical team!`,
    service: "General Dentistry",
  },
  {
    name: "Bair B.",
    rating: 5,
    text: `Everything was very clean, the equipment brand new. First, they took X-rays, then I had a dental cleaning. The doctor who did the cleaning was incredibly professional—before starting, she explained everything to me in detail and did an amazing job. After that, another doctor came in, also unbelievably professional, who treated my tooth and placed a filling—quickly and with high quality! The receptionist was a very polite young woman. Thank you so much for this experience!`,
    service: "Dental Cleaning",
  },
  {
    name: "Arian J.",
    rating: 5,
    text: `I've been going to Dr. Alina for years and she's by far the best dentist I've ever had. She has always provided professional, thorough, and compassionate care. She takes the time to go through x-rays and explain everything, ensuring I'm informed as a patient. When I first saw Dr. Alina years ago, I had 11 cavities. She had them all filled perfectly within the week. The color matching was exact, and years later they've never bothered me or decayed in any way.

Benson Dental Studio, her new office, is incredible. The interior is simple and clean while the equipment is all brand new. The staff is professional and caring. I'll gladly make the trip down from White Plains every time I need to go to the dentist because getting the best care is always worth it. Highly recommend Benson Dental Studio and Dr. Alina.`,
    service: "Dental Crowns",
  },
  {
    name: "Ekaterina S.",
    rating: 5,
    text: `We’ve been looking for a good dental clinic for a long time, and finally found the best one! Dr. Alina is a true professional–she works quickly, efficiently, and with great care. Our whole family is treated by her, and we couldn’t be happier. Highly recommend!`,
    service: "General Dentistry",
  },
  {
    name: "Olga B.",
    rating: 5,
    text: `Recently, my family and I went to the Benson Dental Studio for teeth cleaning and cavity fill. We had a wonderful experience, the staff was very respectful and the doctor was helpful, answered all our questions, and did a wonderful job with our teeth work. I will definitely be coming back and would recommend to all my friends and family.`,
    service: "Family Dentistry",
  },
  {
    name: "Liya Z.",
    rating: 5,
    text: `Wonderful staff, very warm and understanding. The office is incredibly clean and modern. I was in and out quickly, and it was so much better than my experience with other dentists. Will definitely be coming back in the future, thank you!`,
    service: "Emergency Care",
  },
];

const StarRating = ({ rating }) => {
  return (
    <div
      className="star-rating"
      role="img"
      aria-label={`${rating} out of 5 stars`}
    >
      {[...Array(5)].map((_, index) => (
        <Icon key={index} icon={index < rating ? faStar : faStarOutline} />
      ))}
    </div>
  );
};

const TestimonialCard = ({ testimonial, isActive }) => {
  return (
    <article className={`testimonial-card ${isActive ? "active" : ""}`}>
      <StarRating rating={testimonial.rating} />
      <blockquote className="testimonial-text">"{testimonial.text}"</blockquote>
      <footer className="testimonial-footer">
        <cite className="testimonial-name">{testimonial.name}</cite>
        <span className="testimonial-service">{testimonial.service}</span>
      </footer>
    </article>
  );
};

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  }, []);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      goToNext();
    }, 6000);

    return () => clearInterval(interval);
  }, [isPaused, goToNext]);

  return (
    <section
      className="testimonials-section"
      aria-labelledby="testimonials-heading"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <h2 id="testimonials-heading">What Our Patients Say</h2>
      <p className="testimonials-subtitle">
        We're proud to serve the Bensonhurst, Bay Ridge, and Sheepshead Bay
        communities
      </p>

      <div
        className="testimonials-carousel"
        role="region"
        aria-label="Patient testimonials"
      >
        <div className="testimonials-carousel-container">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              testimonial={testimonial}
              isActive={index === currentIndex}
            />
          ))}
        </div>
      </div>

      <div className="carousel-nav">
        <button
          className="carousel-arrow"
          onClick={goToPrev}
          aria-label="Previous testimonial"
        >
          <Icon icon={faChevronLeft} />
        </button>

        <div
          className="carousel-dots"
          role="tablist"
          aria-label="Testimonial navigation"
        >
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? "active" : ""}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to testimonial ${index + 1}`}
              aria-selected={index === currentIndex}
              role="tab"
            />
          ))}
        </div>

        <button
          className="carousel-arrow"
          onClick={goToNext}
          aria-label="Next testimonial"
        >
          <Icon icon={faChevronRight} />
        </button>
      </div>
    </section>
  );
};

export default Testimonials;
